import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../share-components/AppContext';
import "./OrderAvailableProduct.scss";
import OrderAvailableProduct from './OrderAvailableProduct';
import { Fab, Badge } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { customStorage, formatDateTime, getPayloadData, scrollToFormError, scrollToTop } from '../../services/UtilityService';
import { DELI_TYPE_DEFINE, DELI_TYPES, ORDER_STATUS, ORDER_STATUS_DEFINE, PAYMENT_BANKS, SCROLL_RESTORE_PREFIX_KEY, SELECT_DEFAULT, SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import { editOrder, getOrderDetailsToEdit } from '../../services/ManagementService';
import { PAGE_ORDER_EDIT, ROUTE_ORDER_DETAILS } from '../../constants/routeConstants';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import AlertDialog from '../../share-components/AlertDialog';
import FormTitle from '../../share-components/FormTitle';
import { getAllActiveUsers } from '../../services/CommonService';

const OrderEdit = () => {

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const { orderId } = useParams();
    const [isOrderCardShow, setIsOrderCardShow] = useState(true);
    const [formValues, setFormValues] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [originalOrderCartData, setOriginalOrderCartData] = useState([]);
    const [orderCartData, setOrderCartData] = useState([]);
    const [activeUsers, setActiveUsers] = useState([]);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [prGiftAmount, setPrGiftAmount] = useState(0);
    const [serviceCharge, setServiceCharge] = useState("");
    const [tipMoney, setTipMoney] = useState("");
    const [subtotalAmount, setSubtotalAmount] = useState(0);
    const [paymentReceivedAmount, setPaymentReceivedAmount] = useState(0);
    const [orderedDate, setOrderedDate] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [trackerOrderOriginal, setTrackerOrderOriginal] = useState(null);
    const [trackerOrderUpdate, setTrackerOrderUpdate] = useState(null);
    const [parentLoaded, setParentLoaded] = useState(false);
    const [oldOrderStatus, setOldOrderStatus] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        setRouteNaviData("ORDER >> EDIT ORDER");
        sessionStorage.removeItem(`${SCROLL_RESTORE_PREFIX_KEY}${PAGE_ORDER_EDIT}`);
        fetchActiveUsers();
        fetchOrderEditInitialData();
    }, []);

    const fetchActiveUsers = async () => {
        setLoading(true);
        try {
            const response = await getAllActiveUsers();
            setActiveUsers(response?.sort((a, b) => a?.username?.localeCompare(b?.username)) || []);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
            setLoading(false);
        }
    }


    const fetchOrderEditInitialData = async () => {
        setLoading(true);
        try {
            const response = await getOrderDetailsToEdit(orderId);
            if (response?.orderInfo?.orderedById !== currentUser?.userId || response?.orderInfo?.orderStatus === ORDER_STATUS_DEFINE.finished.value ||
                response?.orderInfo?.orderStatus === ORDER_STATUS_DEFINE.cancelled.value) {
                navigate(`${ROUTE_ORDER_DETAILS}/${orderId}`);
                return;
            }

            setOldOrderStatus(response?.orderInfo?.orderStatus);
            setOriginalOrderCartData(response.orderCartData || []);
            setFormValues(response?.orderInfo);
            setOrderedDate(response?.orderInfo?.createdDateTime || '')
            setDeliveryFee(parseInt(response?.orderInfo?.deliveryFee) || 0);
            setPrGiftAmount(parseInt(response?.orderInfo?.prGiftAmount) || 0);
            setServiceCharge(parseInt(response?.orderInfo?.serviceCharge) || 0);
            setTipMoney(parseInt(response?.orderInfo?.tipMoney) || 0);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        if (parentLoaded) {
            setLoading(false);
        }
    }, [parentLoaded])

    useEffect(() => {
        const totalOrderAmount = (orderCartData || []).reduce((sum, order) => sum + (parseInt(order.salePrice) * parseInt(order.orderedQuantity)), 0) || 0;
        setSubtotalAmount(totalOrderAmount);

        setTrackerOrderUpdate(getTrackData(orderCartData));
    }, [orderCartData]);

    useEffect(() => {
        const receivedAmount = subtotalAmount + (parseInt(deliveryFee) || 0) + (parseInt(serviceCharge) || 0) + (parseInt(tipMoney) || 0) - (parseInt(prGiftAmount) || 0);
        setPaymentReceivedAmount(receivedAmount);
    }, [subtotalAmount, deliveryFee, prGiftAmount, serviceCharge, tipMoney]);


    useEffect(() => {
        if (isOrderCardShow) {
            scrollToTop();
        }
    }, [isOrderCardShow]);

    useEffect(() => {
        setTrackerOrderOriginal(getTrackData(originalOrderCartData));
    }, [originalOrderCartData]);

    const getTrackData = (orderCartArray) => {
        if (!Array.isArray(orderCartArray)) {
            return '';
        }
        return [...orderCartArray] // <- copy the array first
            .sort((a, b) =>
                a.inventoryItemId - b.inventoryItemId ||
                a.orderedQuantity - b.orderedQuantity ||
                a.salePrice - b.salePrice
            )
            .map(item => `${item.inventoryItemId}${item.orderedQuantity}${item.salePrice}`)
            .join('') || '';
    };

    const initialValues = {
        customerName: formValues?.customerName || '',
        customerAddress: formValues?.customerAddress || '',
        orderStatus: formValues?.orderStatus || '',
        deliveryType: formValues?.deliveryType || '',
        deliveryFee: formValues?.deliveryFee || '',
        deliveredById: formValues?.deliveredById || '',
        prGiftFlag: formValues?.prGiftFlag || false,
        prGiftAmount: formValues?.prGiftAmount || '',
        serviceCharge: formValues?.serviceCharge || '',
        tipMoney: formValues?.tipMoney || '',
        paymentReceivedById: formValues?.paymentReceivedById || '',
        receivedBankName: formValues?.receivedBankName || '',
        shippingTrackerLink: formValues?.shippingTrackerLink || '',
        comment: formValues?.comment || ''
    };

    const validationSchema = Yup.object({
        customerName: Yup.string()
            .max(30, 'Customer Name must not exceed 30 characters.')
            .required('Customer Name is required.'),
        customerAddress: Yup.string().nullable()
            .max(150, 'Address must not exceed 150 characters.'),
        orderStatus: Yup.string().nullable()
            .required('Order Status is required.'),
        deliveryType: Yup.string().nullable()
            .required('Delivery Type is required.'),
        deliveryFee: Yup.number().nullable()
            .max(100000, 'Amount must not exceed 100000¥.')
            .when("deliveryType", {
                is: (value) => (parseInt(value) || null) === DELI_TYPE_DEFINE.prepaidDelivery.value,
                then: (schema) => schema.required("Delivery Fee is required."),
                otherwise: (schema) => schema.nullable()
            }),
        deliveredById: Yup.string().nullable()
            .when("orderStatus", {
                is: (value) => checkIsRequireDeliveredBy(value),
                then: (schema) => schema.required("Delivered By is required."),
                otherwise: (schema) => schema.nullable()
            }),
        prGiftFlag: Yup.boolean(),
        prGiftAmount: Yup.number().nullable()
            .max(100000, 'Amount must not exceed 100000¥.')
            .when('prGiftFlag', (prGiftFlag, schema) => {
                const flag = Array.isArray(prGiftFlag) ? prGiftFlag[0] : prGiftFlag;
                if (flag) {
                    return schema
                        .required("PR Gift Amount is required.")
                        .test(
                            'does-not-exceed-subtotal', 'PR Gift Price must not exceed (Delivery Fee + Subtotal Price).',
                            function (value) {
                                const gift = parseInt(value) || 0;
                                const fee = parseInt(deliveryFee) || 0;
                                const subtotal = parseInt(subtotalAmount) || 0;

                                return gift - fee <= subtotal;
                            }
                        )
                }
                return schema;
            }),
        serviceCharge: Yup.number().nullable()
            .max(100000, 'Service Charge must not exceed 100000¥.'),
        tipMoney: Yup.number().nullable()
            .max(100000, 'Tip Money must not exceed 100000¥.'),
        paymentReceivedById: Yup.string().nullable()
            .when("orderStatus", {
                is: (value) => checkIsRequiredPaymentReceivedBy(value),
                then: (schema) => schema.required("Payment Received By is required."),
                otherwise: (schema) => schema.nullable()
            }),
        receivedBankName: Yup.string().nullable()
            .max(30, 'Bank Name must not exceed 30 characters.')
            .when("orderStatus", {
                is: (value) => checkIsRequiredPaymentReceivedBy(value),
                then: (schema) => schema.required("Received Bank Name is required."),
                otherwise: (schema) => schema.nullable()
            }),
        shippingTrackerLink: Yup.string().nullable()
            .max(500, 'Shipping Tracker Link must not exceed 500 characters.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.'),

    });

    const checkIsRequireDeliveredBy = (orderStatus) => {
        return parseInt(orderStatus) === ORDER_STATUS_DEFINE.shipped.value || parseInt(orderStatus) === ORDER_STATUS_DEFINE.finished.value
    }

    const checkIsRequiredPaymentReceivedBy = (orderStatus) => {
        return (parseInt(orderStatus) === ORDER_STATUS_DEFINE.paid.value || parseInt(orderStatus) === ORDER_STATUS_DEFINE.shipped.value ||
            parseInt(orderStatus) === ORDER_STATUS_DEFINE.finished.value) && (parseInt(paymentReceivedAmount) || 0) > 0;
    }

    const onSubmit = async (form) => {
        setFormValues(form);
        setShowConfirmDialog(true);
    };

    const handleEditOrder = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const formData = {
                orderInfo: {
                    orderId: parseInt(orderId) || null,
                    customerName: formValues?.customerName || null,
                    customerAddress: formValues?.customerAddress || null,
                    orderStatus: parseInt(formValues.orderStatus) || null,
                    deliveryType: parseInt(formValues?.deliveryType) || null,
                    deliveryFee: parseInt(formValues?.deliveryFee) || 0,
                    deliveredById: formValues?.deliveredById || null,
                    prGiftFlag: formValues?.prGiftFlag || false,
                    prGiftAmount: (formValues?.prGiftFlag === true && parseInt(formValues?.prGiftAmount)) || 0,
                    serviceCharge: parseInt(formValues?.serviceCharge) || 0,
                    tipMoney: parseInt(formValues?.tipMoney) || 0,
                    paymentReceivedById: (checkIsRequiredPaymentReceivedBy(formValues?.orderStatus) && formValues?.paymentReceivedById) || null,
                    receivedBankName: (checkIsRequiredPaymentReceivedBy(formValues?.orderStatus) && formValues?.receivedBankName) || null,
                    shippingTrackerLink: formValues?.shippingTrackerLink || null,
                    comment: formValues?.comment || null
                },
                orderCartData: orderCartData?.map(order => {
                    const data = {
                        inventoryItemId: parseInt(order.inventoryItemId) || null,
                        orderedQuantity: parseInt(order.orderedQuantity) || null,
                        salePrice: parseInt(order.salePrice) || null
                    }
                    return data;
                }) || []
            }
            await editOrder(formData);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        navigate(`${ROUTE_ORDER_DETAILS}/${orderId}`)
    }

    const handleStateChange = (value, setFieldValue, validateForm) => {
        const inputValueState = parseInt(value) || 0;
        if (inputValueState === 0) {
            setFieldValue("deliveredById", "");
            setFieldValue("deliveryFee", "");
            setDeliveryFee("");
            setTimeout(() => {
                validateForm();
            }, 50);
        }
    }

    const handleDeliveryTypeChange = (value, setFieldValue, validateForm) => {
        const inputDeliType = parseInt(value) || 0;
        if (inputDeliType !== DELI_TYPE_DEFINE.prepaidDelivery.value) {
            setFieldValue("deliveryFee", "");
            setDeliveryFee("");
            setTimeout(() => {
                validateForm();
            }, 50);
        }
    }

    const getOrderStatusList = () => {
        const oldStatusNumber = parseInt(oldOrderStatus) || 0;
        // const statusShippingNumber = ORDER_STATUS_DEFINE.shipped.value;
        const orderStatusOptions = ORDER_STATUS.filter(status => status.value >= oldStatusNumber
            &&
            status.value !== ORDER_STATUS_DEFINE.finished.value
            &&
            status.value !== ORDER_STATUS_DEFINE.cancelled.value);
        return orderStatusOptions;
    }

    return (
        <div id='editOrderPage' style={{ paddingBottom: '50px', display: `${parentLoaded ? 'block' : 'none'}` }}>
            <FormTitle title={"Edit Order"} isCenter={true} />
            {isFirstLoaded && <>
                <Card style={{
                    padding: '10px', margin: '0 auto', maxWidth: '650px', marginBottom: '10px'
                }}>
                    <Card.Body className='pt-2'>
                        <div style={{ maxWidth: "550px", margin: '0 auto' }}>
                            <OrderAvailableProduct
                                originalOrderCartData={originalOrderCartData}
                                setParentOrderCart={(orderCart) => setOrderCartData(orderCart)}
                                setParentOrderCartShow={() => setIsOrderCardShow(true)}
                                setParentOrderCartHide={() => setIsOrderCardShow(false)}
                                showOrderCart={isOrderCardShow}
                                setParentLoaded={setParentLoaded}
                                pageName={PAGE_ORDER_EDIT}
                                isForEdit={true}
                            />
                        </div>
                    </Card.Body>
                </Card>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validate={(values) => {
                        setFormValues(values);
                    }}
                    onSubmit={onSubmit}>
                    {({ values, setFieldValue, setFieldTouched, errors, isValid, dirty, validateForm }) => {
                        const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                        return (
                            <Form>
                                <Card style={{
                                    padding: '10px', margin: '0 auto', maxWidth: '650px',
                                    display: `${isOrderCardShow ? 'block' : 'none'}`
                                }}>
                                    <Card.Body className='pt-2'>
                                        <div className="container">
                                            <div className="">

                                                <div style={{ maxWidth: "550px", margin: '0 auto' }}>
                                                    <div>
                                                        <div className="mb-1">
                                                            <label className="custom-label">Ordered Date</label>
                                                            <div className="w-100 label-box">
                                                                {formatDateTime(orderedDate)}
                                                            </div>
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Customer Name*</label>
                                                            <Field name="customerName" type="text" className="w-100 customInputControl" autoComplete="off" />
                                                            <ErrorMessage name="customerName" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Customer Address</label>
                                                            <Field name="customerAddress" component="textarea" rows="4" className="w-100 customInputControl" />
                                                            <ErrorMessage name="customerAddress" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Order Status*</label>
                                                            <Field as="select" name="orderStatus" className="w-100 customInputControl"
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setFieldValue("orderStatus", inputValue);
                                                                    handleStateChange(inputValue, setFieldValue, validateForm);
                                                                }}
                                                            >
                                                                <option value="" disabled>{SELECT_DEFAULT}</option>
                                                                {getOrderStatusList()?.map(status => (
                                                                    <option key={status.value} value={status.value}>{status.text}</option>
                                                                ))}
                                                            </Field>
                                                            <ErrorMessage name="orderStatus" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        {/* {!isNullOrEmptyString(values?.orderStatus) && <> */}
                                                        <div className="mb-1">
                                                            <label className="custom-label">Delivery Type*</label>
                                                            <Field as="select" name="deliveryType" className="w-100 customInputControl"
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setFieldValue("deliveryType", inputValue);
                                                                    handleDeliveryTypeChange(inputValue, setFieldValue, validateForm);
                                                                }}
                                                            >
                                                                <option value="">{SELECT_DEFAULT}</option>
                                                                {DELI_TYPES.map(deliType => (
                                                                    <option key={deliType.value} value={deliType.value}>{deliType.text}</option>
                                                                ))}
                                                            </Field>
                                                            <ErrorMessage name="deliveryType" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        {(parseInt(values.deliveryType) || null) === DELI_TYPE_DEFINE.prepaidDelivery.value && <div className="mb-1">
                                                            <label className="custom-label">Delivery Fee</label>
                                                            <Field name="deliveryFee" type="number"
                                                                inputMode="numeric"  // forces numeric keypad on mobile
                                                                pattern="[0-9]*"      // enforces only digits
                                                                className="w-100 customInputControl clearNumberSpin"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setDeliveryFee(inputValue);
                                                                    setFieldValue("deliveryFee", inputValue);
                                                                }}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                            <ErrorMessage name="deliveryFee" component="div" className="formErrDiv text-danger" />
                                                        </div>}

                                                        <div className="mb-1">
                                                            <label className="custom-label">Delivered By{checkIsRequireDeliveredBy(values.orderStatus) ? '*' : ''}</label>
                                                            <Field as="select" name="deliveredById" className="w-100 customInputControl">
                                                                <option value="">{SELECT_DEFAULT}</option>
                                                                {activeUsers.map(activeUser => (
                                                                    <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                                ))}
                                                            </Field>
                                                            <ErrorMessage name="deliveredById" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Shipping Tracker Link</label>
                                                            <Field name="shippingTrackerLink" component="textarea" rows="3" className="w-100 customInputControl" />
                                                            <ErrorMessage name="shippingTrackerLink" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Subtotal Price</label>
                                                            <div className="w-100 label-box">
                                                                {subtotalAmount}¥
                                                            </div>
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Service Charge</label>
                                                            <Field name="serviceCharge" type="number"
                                                                inputMode="numeric"  // forces numeric keypad on mobile
                                                                pattern="[0-9]*"      // enforces only digits
                                                                className="w-100 customInputControl clearNumberSpin"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setServiceCharge(inputValue);
                                                                    setFieldValue("serviceCharge", inputValue);
                                                                }}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                            <ErrorMessage name="serviceCharge" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mb-1">
                                                            <label className="custom-label">Tip Money</label>
                                                            <Field name="tipMoney" type="number"
                                                                inputMode="numeric"  // forces numeric keypad on mobile
                                                                pattern="[0-9]*"      // enforces only digits
                                                                className="w-100 customInputControl clearNumberSpin"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setTipMoney(inputValue);
                                                                    setFieldValue("tipMoney", inputValue);
                                                                }}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                            <ErrorMessage name="tipMoney" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                            <label className="form-check-label custom-label">
                                                                PR Gift Include?
                                                                <Field type="checkbox" name="prGiftFlag" className="form-check-input ms-2"
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        setPrGiftAmount("");
                                                                        setFieldValue("prGiftFlag", isChecked);
                                                                        setTimeout(() => {
                                                                            setFieldValue("prGiftAmount", "");
                                                                            setTimeout(() => {
                                                                                setFieldTouched("prGiftAmount");
                                                                            }, 100);
                                                                        }, 100);
                                                                    }}
                                                                />
                                                            </label>
                                                            <ErrorMessage name="prGiftFlag" component="div" className="formErrDiv text-danger" />
                                                        </div>

                                                        {values.prGiftFlag === true && <div className="mb-1">
                                                            <label className="custom-label">PR Gift Price*</label>
                                                            <Field name="prGiftAmount" type="number"
                                                                inputMode="numeric"  // forces numeric keypad on mobile
                                                                pattern="[0-9]*"      // enforces only digits
                                                                className="w-100 customInputControl clearNumberSpin"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    setPrGiftAmount(inputValue);
                                                                    setFieldValue("prGiftAmount", inputValue);
                                                                }}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                            <ErrorMessage name="prGiftAmount" component="div" className="formErrDiv text-danger" />
                                                        </div>}

                                                        <div className="mb-1">
                                                            <label className="custom-label">Actual Payment Received Price</label>
                                                            <div className="w-100 label-box">
                                                                {paymentReceivedAmount}¥
                                                            </div>
                                                        </div>

                                                        {checkIsRequiredPaymentReceivedBy(values?.orderStatus) && <>
                                                            <div className="mb-1">
                                                                <label className="custom-label">Payment Received By*</label>
                                                                <Field as="select" name="paymentReceivedById" className="w-100 customInputControl">
                                                                    <option value="">{SELECT_DEFAULT}</option>
                                                                    {activeUsers.map(activeUser => (
                                                                        <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                                    ))}
                                                                </Field>
                                                                <ErrorMessage name="paymentReceivedById" component="div" className="formErrDiv text-danger" />
                                                            </div>

                                                            <div className="mb-1">
                                                                <label className="custom-label">Received Bank Name*</label>
                                                                <Field as="select" name="receivedBankName" className="w-100 customInputControl">
                                                                    <option value="">{SELECT_DEFAULT}</option>
                                                                    {PAYMENT_BANKS.map((bankName, index) => (
                                                                        <option key={index} value={bankName}>{bankName}</option>
                                                                    ))}
                                                                </Field>
                                                                {/* <Field name="receivedBankName" type="text" className="w-100 customInputControl" autoComplete="off" /> */}
                                                                <ErrorMessage name="receivedBankName" component="div" className="formErrDiv text-danger" />
                                                            </div>
                                                        </>}

                                                        <div className="mb-1">
                                                            <label className="custom-label">Comment</label>
                                                            <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" />
                                                            <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <ConfirmDialog
                                                    dialogId='editOrderConfirmDialog'
                                                    showDialog={showConfirmDialog}
                                                    // title='Logout Dialog'
                                                    body='Are you sure to change the order info?'
                                                    onConfirm={handleEditOrder}
                                                    onCancel={() => { setFormValues(null); setShowConfirmDialog(false); }}
                                                />
                                                <AlertDialog
                                                    dialogId={'successDialog'}
                                                    showDialog={showSuccessDialog}
                                                    body={'Order is successfully edited.'}
                                                    onConfirm={handleSuccessDialogClose}
                                                />

                                                <div>
                                                    <Fab
                                                        color="warning"
                                                        sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 1 }}
                                                        onClick={() => setIsOrderCardShow(false)}
                                                    >
                                                        <Badge badgeContent={"+"} color="primary">
                                                            <ShoppingCartIcon />
                                                        </Badge>
                                                    </Fab>
                                                </div>
                                                <div className='floating-bottom-btn-group mt-2'>
                                                    <div className='floating-bottom-btn-grid'>
                                                        <Button
                                                            className="w-100"
                                                            type="submit"
                                                            variant="primary"
                                                            disabled={!orderCartData || orderCartData.length <= 0 || ((trackerOrderOriginal === trackerOrderUpdate) && !dirty)}
                                                            onClick={scrollToFormError}
                                                        >
                                                            Change
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Form>
                        );
                    }}
                </Formik>
            </>}
        </div>
    );
};

export default OrderEdit;